import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Lane, LanePath, SimChampion, CELL_SIZE } from './simulationTypes';
import type { Minion, Tower } from './simulationTypes';
import SimulatorMap, { SimulatorMapHandle } from './SimulatorMap';
import { useSimulation } from './useSimulation';
import { initLanePaths, LANE_PATHS } from './lanePaths';
import { killJungleCamp } from './minionLogic';
import { useJungleMode } from './useJungleMode';
import JungleModePanel from './JungleModePanel';
import './RiftSimulatorPage.css';

/* ══════════════════ Helpers ══════════════════ */

/** Format seconds → "M:SS" */
function fmtTime(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const SPEEDS = [1, 2, 4, 8, 16, 32];

/** Default sim speed while a champion is moving to a destination */
const DEFAULT_MOVE_SIM_SPEED = 16;

const API_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/riftmap`;

/* ── Pathfinding ── */

interface PathResult {
  path: { x: number; y: number }[];
  travelTimeSeconds: number;
}

async function fetchPath(
  startGx: number,
  startGy: number,
  endGx: number,
  endGy: number,
  moveSpeed: number,
): Promise<PathResult> {
  try {
    const res = await fetch(`${API_BASE}/pathfind`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        startX: Math.round(startGx),
        startY: Math.round(startGy),
        endX: Math.round(endGx),
        endY: Math.round(endGy),
        movementSpeed: moveSpeed,
      }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    return {
      path: data.path ?? [{ x: endGx, y: endGy }],
      travelTimeSeconds: data.travelTimeSeconds ?? 0,
    };
  } catch (err) {
    console.error('fetchPath error:', err, { startGx, startGy, endGx, endGy });
    // Re-throw — caller must handle (undo snapshot, cancel action)
    throw err;
  }
}

/* ── Movement order ── */

interface MoveOrder {
  champId: number;
  path: { x: number; y: number }[];
  wpIdx: number;
  /** Called when the champion arrives at the destination */
  onArrive?: () => void;
  /** Sim speed to restore after arrival */
  prevSpeed: number;
  /** Whether the sim was already running before the move was issued */
  wasRunning: boolean;
}

/* ══════════════════ Default team setups ══════════════════ */

const DEFAULT_BLUE: Omit<SimChampion, 'id'>[] = [
  { name: 'Top',     team: 'blue', role: 'top',     gx: 42,  gy: 165, moveSpeed: 350, color: '#4a9eff' },
  { name: 'Jungle',  team: 'blue', role: 'jungle',  gx: 395, gy: 258, moveSpeed: 350, color: '#4a9eff' },
  { name: 'Mid',     team: 'blue', role: 'mid',     gx: 215, gy: 300, moveSpeed: 350, color: '#4a9eff' },
  { name: 'ADC',     team: 'blue', role: 'adc',     gx: 360, gy: 468, moveSpeed: 350, color: '#4a9eff' },
  { name: 'Support', team: 'blue', role: 'support', gx: 353, gy: 462, moveSpeed: 350, color: '#4a9eff' },
];

const DEFAULT_RED: Omit<SimChampion, 'id'>[] = [
  { name: 'Top',     team: 'red', role: 'top',     gx: 155, gy: 42,  moveSpeed: 350, color: '#ff6b6b' },
  { name: 'Jungle',  team: 'red', role: 'jungle',  gx: 120, gy: 256, moveSpeed: 350, color: '#ff6b6b' },
  { name: 'Mid',     team: 'red', role: 'mid',     gx: 302, gy: 218, moveSpeed: 350, color: '#ff6b6b' },
  { name: 'ADC',     team: 'red', role: 'adc',     gx: 470, gy: 352, moveSpeed: 350, color: '#ff6b6b' },
  { name: 'Support', team: 'red', role: 'support', gx: 462, gy: 346, moveSpeed: 350, color: '#ff6b6b' },
];

/* ══════════════════ Component ══════════════════ */

const RiftSimulatorPage: React.FC = () => {
  const mapRef = useRef<SimulatorMapHandle>(null);
  const [lanePaths, setLanePaths] = useState<Record<Lane, LanePath> | null>(null);
  const [pathsLoading, setPathsLoading] = useState(true);

  /* ── Champions state ── */
  const [champions, setChampions] = useState<SimChampion[]>([]);
  const [selectedChampionId, setSelectedChampionId] = useState<number | null>(null);

  /** Sim speed for jungle mode animations (user-adjustable) */
  const [jungleSimSpeed, setJungleSimSpeed] = useState(DEFAULT_MOVE_SIM_SPEED);
  const jungleSimSpeedRef = useRef(DEFAULT_MOVE_SIM_SPEED);
  const champIdRef = useRef(1);

  /* Refs so frame / async callbacks always read current values */
  const championsRef = useRef(champions);
  useEffect(() => { championsRef.current = champions; });
  const selectedIdRef = useRef(selectedChampionId);
  useEffect(() => { selectedIdRef.current = selectedChampionId; });

  /* Active movement order */
  const moveOrderRef = useRef<MoveOrder | null>(null);
  /** Remember the *original* speed before the first move in a chain */
  const preMoveSavedRef = useRef<{ speed: number; wasRunning: boolean } | null>(null);

  /* Active channel order — used for recall and camp clearing in jungle mode */
  const channelOrderRef = useRef<{
    /** Game time remaining to channel */
    remaining: number;
    /** Called when channel finishes */
    onComplete: () => void;
    /** If true, block movement during channel (recall). If false, allow (clearing). */
    blockMovement: boolean;
  } | null>(null);

  /** Pending clear — set when walking to a camp, triggers channel when entering leash range */
  const pendingClearRef = useRef<{
    campId: string;
    campGx: number;
    campGy: number;
    destGx: number;
    destGy: number;
    gameTimeBefore: number;
    leashRangeGrid: number;
  } | null>(null);

  /* Jungle mode action queue (up to 10 chained camp clicks) */
  const campQueueRef = useRef<Array<{ campId: string; gx?: number; gy?: number }>>([]);
  const MAX_QUEUE = 10;

  /* Ref-function called every rAF frame to advance champion along its path.
     Assigned during render so it always captures latest closures. */
  const advanceRef = useRef<(simDt: number) => void>(() => {});

  /* ── Load lane paths ── */
  useEffect(() => {
    initLanePaths().then(() => {
      setLanePaths({ ...LANE_PATHS });
      setPathsLoading(false);
    });
  }, []);

  /* ── Simulation hook ── */
  const onFrame = useCallback(
    (minions: Minion[], towers: Tower[], simDt: number) => {
      mapRef.current?.renderMinions(minions, towers);
      advanceRef.current(simDt);
    },
    [],
  );

  const sim = useSimulation(onFrame);
  const { display, togglePlay, reset, setSpeed, play, pause, refreshDisplay } = sim;

  /* ── Jungle Mode ── */
  const jungleMode = useJungleMode();

  /* Auto-activate jungle mode on first render */
  const didAutoActivate = useRef(false);
  useEffect(() => {
    if (didAutoActivate.current || jungleMode.isActive) return;
    didAutoActivate.current = true;
    const cfg = jungleMode.config;
    const blueJungler: SimChampion = {
      id: champIdRef.current++, name: 'Jungle', team: 'blue', role: 'jungle',
      gx: 395, gy: 258, moveSpeed: cfg.moveSpeed, color: '#4a9eff',
    };
    const redJungler: SimChampion = {
      id: champIdRef.current++, name: 'Jungle', team: 'red', role: 'jungle',
      gx: 120, gy: 256, moveSpeed: cfg.moveSpeed, color: '#ff6b6b',
    };
    setChampions([blueJungler, redJungler]);
    setSelectedChampionId(cfg.playerSide === 'blue' ? blueJungler.id : redJungler.id);
    jungleMode.activate(sim.simRef, reset);
    refreshDisplay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ═══════════════════════════════════════════════════════
     Per-frame champion movement  (runs inside rAF via advanceRef)
     ═══════════════════════════════════════════════════════ */
  advanceRef.current = (simDt: number) => {
    if (simDt <= 0) return;

    /* ── Channel (recall / camp clearing) ── */
    const channel = channelOrderRef.current;
    if (channel) {
      channel.remaining -= simDt;
      if (channel.remaining <= 0) {
        channel.onComplete();
        // If no move order left, restore pre-move state
        if (!moveOrderRef.current) {
          const saved = preMoveSavedRef.current;
          if (saved) {
            setSpeed(saved.speed);
            if (!saved.wasRunning) pause();
          }
          preMoveSavedRef.current = null;
        }
        channelOrderRef.current = null;
        refreshDisplay();
      }
      // If channel blocks movement (recall), skip movement processing
      if (channel.blockMovement) return;
    }

    /* ── Movement ── */
    const order = moveOrderRef.current;
    if (!order) return;

    const champ = championsRef.current.find((c) => c.id === order.champId);
    if (!champ) {
      moveOrderRef.current = null;
      preMoveSavedRef.current = null;
      return;
    }

    const moveSpeedGrid = champ.moveSpeed / CELL_SIZE;
    let budget = moveSpeedGrid * simDt;
    let cx = champ.gx;
    let cy = champ.gy;

    while (budget > 0.001 && order.wpIdx < order.path.length) {
      const wp = order.path[order.wpIdx];
      const dx = wp.x - cx;
      const dy = wp.y - cy;
      const dist = Math.hypot(dx, dy);
      if (dist <= budget) {
        cx = wp.x;
        cy = wp.y;
        budget -= dist;
        order.wpIdx++;
      } else {
        const r = budget / dist;
        cx += dx * r;
        cy += dy * r;
        budget = 0;
      }
    }

    setChampions((prev) =>
      prev.map((c) =>
        c.id === order.champId ? { ...c, gx: cx, gy: cy } : c,
      ),
    );

    /* ── Check if champion entered camp leash range (start clearing early) ── */
    const pending = pendingClearRef.current;
    if (pending && !channelOrderRef.current) {
      const distToCamp = Math.hypot(cx - pending.campGx, cy - pending.campGy);
      if (distToCamp <= pending.leashRangeGrid) {
        // Entered leash range — start clearing NOW while still moving
        const walkTimeElapsed = sim.simRef.current.gameTime - pending.gameTimeBefore;
        const clearTime = jungleMode.getClearTime(pending.campId, sim.simRef);

        if (clearTime > 0) {
          jungleMode.setClearingCampId(pending.campId);
          const campId = pending.campId;
          const destGx = pending.destGx;
          const destGy = pending.destGy;
          const gameTimeBefore = pending.gameTimeBefore;

          channelOrderRef.current = {
            remaining: clearTime,
            blockMovement: false, // keep moving during clear
            onComplete: () => {
              const clearElapsed = sim.simRef.current.gameTime - gameTimeBefore - walkTimeElapsed;
              jungleMode.completeStep(campId, sim.simRef, walkTimeElapsed, clearElapsed, { gx: destGx, gy: destGy });
              refreshDisplay();
              processNextInQueue();
            },
          };
        } else {
          // Dead camp — just complete walk
          jungleMode.completeStep(pending.campId, sim.simRef, walkTimeElapsed, 0, { gx: pending.destGx, gy: pending.destGy });
          refreshDisplay();
          processNextInQueue();
        }
        pendingClearRef.current = null;
      }
    }

    // Arrived at final destination?
    if (order.wpIdx >= order.path.length) {
      order.onArrive?.();
      moveOrderRef.current = null;

      // If a channel is still running (clear started mid-walk), let it finish
      if (channelOrderRef.current) {
        // Channel will handle speed restore when done
      } else {
        const saved = preMoveSavedRef.current;
        if (saved) {
          setSpeed(saved.speed);
          if (!saved.wasRunning) pause();
        }
        preMoveSavedRef.current = null;
      }
      refreshDisplay();
    }
  };

  /* ═══════════════════════════════════════════════════════
     Issue a move order for a champion
     ═══════════════════════════════════════════════════════ */
  const issueMove = useCallback(
    async (champId: number, destGx: number, destGy: number, onArrive?: () => void) => {
      const champ = championsRef.current.find((c) => c.id === champId);
      if (!champ) return;

      const { path } = await fetchPath(champ.gx, champ.gy, destGx, destGy, champ.moveSpeed);

      // Save the original pre-movement state only if this is the first move
      if (!preMoveSavedRef.current) {
        preMoveSavedRef.current = {
          speed: sim.simRef.current.speed,
          wasRunning: sim.simRef.current.running,
        };
      }

      moveOrderRef.current = {
        champId,
        path,
        wpIdx: 0,
        onArrive,
        prevSpeed: preMoveSavedRef.current.speed,
        wasRunning: preMoveSavedRef.current.wasRunning,
      };

      // Speed up & ensure the sim is playing
      setSpeed(jungleSimSpeedRef.current);
      if (!sim.simRef.current.running) play();
    },
    [sim.simRef, setSpeed, play],
  );

  /* ── Auto-add both teams ── */
  const addTeams = useCallback(() => {
    const teams: SimChampion[] = [
      ...DEFAULT_BLUE.map((c) => ({ ...c, id: champIdRef.current++ })),
      ...DEFAULT_RED.map((c) => ({ ...c, id: champIdRef.current++ })),
    ];
    setChampions(teams);
    setSelectedChampionId(null);
  }, []);

  /* ── Start a single jungle-mode camp action (walk → clear → next in queue) ── */
  const startJungleCampAction = useCallback(
    async (campId: string, clickGx?: number, clickGy?: number) => {
      const camp = sim.campsRef.current.find(c => c.id === campId);
      if (!camp) { processNextInQueue(); return; }

      if (!jungleMode.takeSnapshot(sim.simRef)) { processNextInQueue(); return; }

      const gameTimeBefore = sim.simRef.current.gameTime;

      const playerJungler = championsRef.current.find(
        c => c.role === 'jungle' && c.team === jungleMode.config.playerSide,
      );
      if (!playerJungler) {
        jungleMode.setAnimating(false);
        processNextInQueue();
        return;
      }

      // Always pathfind to camp center (guaranteed walkable).
      // Clearing triggers when entering leash range mid-walk.
      const leashRangeGrid = 600 / CELL_SIZE; // 600 game units
      pendingClearRef.current = {
        campId,
        campGx: camp.gx,
        campGy: camp.gy,
        destGx: camp.gx,
        destGy: camp.gy,
        gameTimeBefore,
        leashRangeGrid,
      };

      try {
      await issueMove(playerJungler.id, camp.gx, camp.gy, () => {
        // Arrived at camp center. If clear already started via leash range, nothing to do.
        // If clear hasn't started yet (very close spawn), start now.
        if (pendingClearRef.current?.campId === campId) {
          const walkTimeElapsed = sim.simRef.current.gameTime - gameTimeBefore;
          const clearTime = jungleMode.getClearTime(campId, sim.simRef);

          if (clearTime > 0) {
            jungleMode.setClearingCampId(campId);
            channelOrderRef.current = {
              remaining: clearTime,
              blockMovement: false,
              onComplete: () => {
                const clearElapsed = sim.simRef.current.gameTime - gameTimeBefore - walkTimeElapsed;
                jungleMode.completeStep(campId, sim.simRef, walkTimeElapsed, clearElapsed, { gx: camp.gx, gy: camp.gy });
                refreshDisplay();
                processNextInQueue();
              },
            };
          } else {
            jungleMode.completeStep(campId, sim.simRef, walkTimeElapsed, 0, { gx: camp.gx, gy: camp.gy });
            refreshDisplay();
            processNextInQueue();
          }
          pendingClearRef.current = null;
        }
        // If pendingClearRef was already consumed (clear started mid-walk), onArrive is a no-op.
        // The channel's onComplete handles processNextInQueue.
      });
      } catch (err) {
        // Pathfinding failed — undo and cancel
        console.error('Camp action failed (pathfinding):', err);
        pendingClearRef.current = null;
        jungleMode.undo(sim.simRef);
        refreshDisplay();
        campQueueRef.current = [];
      }
    },
    [sim.simRef, sim.campsRef, jungleMode, issueMove, refreshDisplay, setSpeed, play],
  );

  /* ── Process next queued camp action ── */
  function processNextInQueue() {
    const next = campQueueRef.current.shift();
    if (next) {
      startJungleCampAction(next.campId, next.gx, next.gy);
    }
  }

  /* ── Camp click → move champion to camp, kill camp on arrival ── */
  const handleCampClick = useCallback(
    async (campId: string, clickGx?: number, clickGy?: number) => {
      /* ── Jungle Mode ── */
      if (jungleMode.isActive) {
        if (jungleMode.animating) {
          // Currently busy — queue the click (up to MAX_QUEUE)
          if (campQueueRef.current.length < MAX_QUEUE) {
            campQueueRef.current.push({ campId, gx: clickGx, gy: clickGy });
          }
          return;
        }

        // Start action immediately
        await startJungleCampAction(campId, clickGx, clickGy);
        return;
      }

      /* ── Normal mode ── */
      const camp = sim.campsRef.current.find((c) => c.id === campId);
      if (!camp || !camp.alive) return;

      let champ: SimChampion | undefined;
      if (selectedIdRef.current !== null) {
        champ = championsRef.current.find((c) => c.id === selectedIdRef.current);
      }
      if (!champ) {
        champ = championsRef.current
          .filter((c) => c.role === 'jungle')
          .sort(
            (a, b) =>
              Math.hypot(a.gx - camp.gx, a.gy - camp.gy) -
              Math.hypot(b.gx - camp.gx, b.gy - camp.gy),
          )[0];
      }

      if (champ) {
        await issueMove(champ.id, camp.gx, camp.gy, () => {
          killJungleCamp(sim.simRef.current, campId);
          refreshDisplay();
        });
      } else {
        killJungleCamp(sim.simRef.current, campId);
        refreshDisplay();
      }
    },
    [sim.simRef, sim.campsRef, issueMove, refreshDisplay, jungleMode],
  );

  /* ── Right-click map → move selected champion there ── */
  const handleMapRightClick = useCallback(
    async (gx: number, gy: number) => {
      // Block right-click movement during jungle mode walk/clear/recall
      if (jungleMode.isActive && jungleMode.animating) return;

      const champId = selectedIdRef.current;
      if (champId === null) return;
      await issueMove(champId, gx, gy);
    },
    [issueMove, jungleMode],
  );

  /* ── Jungle Mode toggle ── */
  const handleJungleModeToggle = useCallback(() => {
    if (jungleMode.isActive) {
      campQueueRef.current = [];
      jungleMode.deactivate();
    } else {
      // Set up jungler champions before activating
      const cfg = jungleMode.config;
      const aiSide = cfg.playerSide === 'blue' ? 'red' : 'blue';
      const blueJungler: SimChampion = {
        id: champIdRef.current++,
        name: 'Jungle',
        team: 'blue',
        role: 'jungle',
        gx: 395,
        gy: 258,
        moveSpeed: cfg.moveSpeed,
        color: '#4a9eff',
      };
      const redJungler: SimChampion = {
        id: champIdRef.current++,
        name: 'Jungle',
        team: 'red',
        role: 'jungle',
        gx: 120,
        gy: 256,
        moveSpeed: cfg.moveSpeed,
        color: '#ff6b6b',
      };
      setChampions([blueJungler, redJungler]);
      setSelectedChampionId(cfg.playerSide === 'blue' ? blueJungler.id : redJungler.id);
      jungleMode.activate(sim.simRef, reset);
    }
  }, [jungleMode, sim.simRef, reset]);

  const handleJungleModeUndo = useCallback(() => {
    campQueueRef.current = [];
    moveOrderRef.current = null;
    channelOrderRef.current = null;
    pendingClearRef.current = null;
    jungleMode.undo(sim.simRef);
    // Restore speed
    const saved = preMoveSavedRef.current;
    if (saved) {
      setSpeed(saved.speed);
      if (!saved.wasRunning) pause();
    }
    preMoveSavedRef.current = null;
    refreshDisplay();
  }, [jungleMode, sim.simRef, refreshDisplay, setSpeed, pause]);

  const handleJungleModeReset = useCallback(() => {
    campQueueRef.current = [];
    moveOrderRef.current = null;
    channelOrderRef.current = null;
    pendingClearRef.current = null;
    preMoveSavedRef.current = null;
    // Re-activate with fresh state
    const cfg = jungleMode.config;
    const blueJungler: SimChampion = {
      id: champIdRef.current++,
      name: 'Jungle',
      team: 'blue',
      role: 'jungle',
      gx: 395,
      gy: 258,
      moveSpeed: cfg.moveSpeed,
      color: '#4a9eff',
    };
    const redJungler: SimChampion = {
      id: champIdRef.current++,
      name: 'Jungle',
      team: 'red',
      role: 'jungle',
      gx: 120,
      gy: 256,
      moveSpeed: cfg.moveSpeed,
      color: '#ff6b6b',
    };
    setChampions([blueJungler, redJungler]);
    setSelectedChampionId(cfg.playerSide === 'blue' ? blueJungler.id : redJungler.id);
    jungleMode.activate(sim.simRef, reset);
    refreshDisplay();
  }, [jungleMode, sim.simRef, reset, refreshDisplay]);

  const handleJungleModeRecall = useCallback(() => {
    if (jungleMode.animating || jungleMode.recalling) return;
    campQueueRef.current = []; // Clear queue on recall

    // Take snapshot for undo
    if (!jungleMode.takeSnapshot(sim.simRef)) return;

    const gameTimeBefore = sim.simRef.current.gameTime;

    // Start recall animation — setRecalling shows the swirl
    jungleMode.setAnimating(true);
    jungleMode.setRecalling(true);

    // Save pre-recall state & speed up sim
    if (!preMoveSavedRef.current) {
      preMoveSavedRef.current = {
        speed: sim.simRef.current.speed,
        wasRunning: sim.simRef.current.running,
      };
    }
    setSpeed(jungleSimSpeedRef.current);
    if (!sim.simRef.current.running) play();

    // Set up recall order — frame loop counts down game-time
    channelOrderRef.current = {
      remaining: jungleMode.config.baseTime,
      blockMovement: true, // stand still while recalling
      onComplete: () => {
        jungleMode.completeRecall(sim.simRef, sim.simRef.current.gameTime - gameTimeBefore);
        refreshDisplay();
      },
    };
  }, [jungleMode, sim.simRef, setSpeed, play, refreshDisplay]);

  /* ── Sync champion positions with jungle mode state ── */
  useEffect(() => {
    if (!jungleMode.isActive) return;
    const cfg = jungleMode.config;
    const aiSide = cfg.playerSide === 'blue' ? 'red' : 'blue';
    setChampions(prev => prev.map(c => {
      if (c.role === 'jungle' && c.team === cfg.playerSide) {
        return { ...c, gx: jungleMode.playerPosition.gx, gy: jungleMode.playerPosition.gy };
      }
      if (c.role === 'jungle' && c.team === aiSide) {
        return { ...c, gx: jungleMode.aiPosition.gx, gy: jungleMode.aiPosition.gy };
      }
      return c;
    }));
  }, [jungleMode.isActive, jungleMode.playerPosition, jungleMode.aiPosition, jungleMode.config]);

  /* ── Build level badges for SimulatorMap ── */
  const levelBadges: Record<number, number> | undefined = jungleMode.isActive
    ? (() => {
        const badges: Record<number, number> = {};
        for (const ch of champions) {
          if (ch.role !== 'jungle') continue;
          if (ch.team === jungleMode.config.playerSide) {
            badges[ch.id] = jungleMode.playerLevel;
          } else {
            badges[ch.id] = jungleMode.aiLevel;
          }
        }
        return badges;
      })()
    : undefined;

  /* ── Camps snapshot (refreshed at display-refresh rate) ── */
  const camps = sim.campsRef.current;

  return (
    <div className="rsp-root">
      {/* ── Toolbar ── */}
      <div className="rsp-toolbar">
        <div className="rsp-toolbar-left">
          <button
            className="rsp-btn rsp-btn-base"
            onClick={handleJungleModeRecall}
            disabled={jungleMode.animating}
          >
            Base
          </button>
          <button
            className="rsp-btn"
            onClick={handleJungleModeReset}
          >
            Reset
          </button>
          <button
            className="rsp-btn"
            onClick={() => mapRef.current?.resetView()}
          >
            Reset View
          </button>
        </div>

        <div className="rsp-toolbar-center">
          <span className="rsp-time">{fmtTime(display.gameTime)}</span>
        </div>

        <div className="rsp-toolbar-right">
          <span className="rsp-label">Speed</span>
          {SPEEDS.map((s) => (
            <button
              key={s}
              className={`rsp-btn rsp-btn-sm${jungleSimSpeed === s ? ' rsp-btn-active' : ''}`}
              onClick={() => {
                jungleSimSpeedRef.current = s;
                setJungleSimSpeed(s);
              }}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      {/* ── Map ── */}
      <section className="rsp-map-section">
        <div className="rsp-map-wrap">
          <SimulatorMap
            ref={mapRef}
            lanePaths={lanePaths}
            camps={camps}
            gameTime={display.gameTime}
            champions={champions}
            selectedChampionId={selectedChampionId}
            onSelectChampion={setSelectedChampionId}
            onCampClick={handleCampClick}
            onMapRightClick={handleMapRightClick}
            jungleModeActive={jungleMode.isActive}
            levelBadges={levelBadges}
            clearingCampId={jungleMode.clearingCampId}
            goldPopup={jungleMode.goldPopup}
            campLevelBadges={jungleMode.campLevels}
            recallingChampId={
              jungleMode.recalling
                ? champions.find(c => c.role === 'jungle' && c.team === jungleMode.config.playerSide)?.id ?? null
                : null
            }
          />
        </div>
        {pathsLoading && (
          <div className="rsp-paths-loading">Loading lane paths…</div>
        )}
      </section>

      {/* ── Jungle Mode Panel ── */}
      <JungleModePanel
        config={jungleMode.config}
        onConfigChange={jungleMode.updateConfig}
        steps={jungleMode.steps}
        onUndo={handleJungleModeUndo}
        canUndo={jungleMode.steps.length > 0}
      />

    </div>
  );
};

export default RiftSimulatorPage;
